import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, {
  formatApiError, tSimState, simStateCls, SIMULATED_STATE, SIMULATED_STATE_KEYS,
  tFreshness, twinFreshnessCls, tDivergence, tTwinSeverity, twinSeverityCls,
  TWIN_CONFIDENCE, TWIN_CONFIDENCE_KEYS, covPct,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { EmptyState } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Radar, Plus, UploadCloud, FileText, HelpCircle, GitCompare, Filter } from "lucide-react";

const EMPTY_OBS = { node_id: "", state: "OPERATIONAL", confidence: "MEDIUM", observed_at: "", note: "" };

function StateCell({ value }) {
  if (!value) return <span className="text-xs text-muted-foreground">—</span>;
  return <Badge variant="outline" className={`rounded-sm text-xs ${simStateCls(value)}`}>{tSimState(value)}</Badge>;
}

function CovCard({ label, value, hint, testid }) {
  return (
    <div className="border border-border rounded-sm bg-card p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{label}</div>
      <div className="mt-1 font-head text-3xl font-bold tabular-nums" data-testid={`${testid}-value`}>{covPct(value)}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>
    </div>
  );
}

export default function DigitalTwin() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const canIngest = can("twin:ingest");

  const [runId, setRunId] = useState("");           // override explicite (vide = dernier run)
  const [runInput, setRunInput] = useState("");
  const [divOnly, setDivOnly] = useState(false);
  const [obsOpen, setObsOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_OBS);
  const [trace, setTrace] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const { data: comp, isLoading } = useQuery({
    queryKey: ["twin-comparison", runId],
    queryFn: async () => (await api.get("/twin/comparison", { params: runId ? { run_id: runId } : {} })).data,
  });

  // Nœuds observables (actifs + services) pour la saisie manuelle.
  const { data: nodes = [] } = useQuery({
    queryKey: ["twin-nodes"],
    enabled: canIngest,
    queryFn: async () => {
      const [a, s] = await Promise.all([api.get("/assets"), api.get("/services")]);
      return [
        ...a.data.map((x) => ({ id: x.id, name: x.name, type: "asset" })),
        ...s.data.map((x) => ({ id: x.id, name: x.name, type: "service" })),
      ];
    },
  });

  const submitObs = useMutation({
    mutationFn: async () => {
      const o = { node_id: form.node_id, state: form.state, source_type: "manual", confidence: form.confidence,
                  event_kind: "observation", note: form.note };
      if (form.observed_at) o.observed_at = new Date(form.observed_at).toISOString();
      return (await api.post("/twin/observations", { observations: [o] })).data;
    },
    onSuccess: (d) => {
      if (d.ingested > 0) { toast.success("Observation enregistrée"); setObsOpen(false); }
      else toast.error(formatApiError(d.errors?.[0]?.error || "Aucune observation enregistrée"));
      qc.invalidateQueries({ queryKey: ["twin-comparison"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const applyRun = () => setRunId(runInput.trim());
  const rows = (comp?.nodes || []).filter((r) => !divOnly || (r.divergence !== "TWIN-DIV-ALIGNED" && r.divergence !== "TWIN-COV-UNKNOWN"));
  const cov = comp?.coverage || {};
  const ref = comp?.reference_run;

  return (
    <AppShell
      title="Jumeau numérique"
      subtitle="Comparaison structurel / observé / simulé — l'état observé est qualifié (jamais des logs bruts)"
      actions={canIngest && (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-sm gap-2" onClick={() => setImportOpen(true)} data-testid="import-observations-btn">
            <UploadCloud className="h-4 w-4" /> Importer CSV/JSON
          </Button>
          <Button size="sm" className="rounded-sm gap-2" onClick={() => { setForm(EMPTY_OBS); setObsOpen(true); }} data-testid="add-observation-btn">
            <Plus className="h-4 w-4" /> Nouvelle observation
          </Button>
        </div>
      )}
    >
      {/* Couverture — 4 métriques SÉPARÉES, aucun score composite */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="twin-coverage">
        <CovCard label="Couverture actifs" value={cov.asset_coverage} testid="twin-cov-asset"
          hint={`${cov.counts?.assets_observed ?? 0}/${cov.counts?.assets_total ?? 0} observés`} />
        <CovCard label="Actifs à jour" value={cov.fresh_asset_coverage} testid="twin-cov-fresh-asset"
          hint={`${cov.counts?.assets_fresh ?? 0}/${cov.counts?.assets_total ?? 0} frais`} />
        <CovCard label="Couverture services" value={cov.service_coverage} testid="twin-cov-service"
          hint={`${cov.counts?.services_observed ?? 0}/${cov.counts?.services_total ?? 0} observés`} />
        <CovCard label="Services à jour" value={cov.fresh_service_coverage} testid="twin-cov-fresh-service"
          hint={`${cov.counts?.services_fresh ?? 0}/${cov.counts?.services_total ?? 0} frais`} />
      </div>

      {/* Barre de run de référence (overlay simulé) — toujours affichée */}
      <div className="border border-border rounded-sm bg-card p-3 mb-4 flex flex-wrap items-center gap-3" data-testid="reference-run-bar">
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          <GitCompare className="h-4 w-4" /> Run simulé de référence :
        </span>
        {ref ? (
          <span className="text-xs" data-testid="reference-run-info">
            <span className="font-mono text-foreground">{ref.run_id.slice(0, 8)}</span>
            <span className="text-muted-foreground"> — {ref.scenario_title || ref.scenario_id} — {ref.created_at?.slice(0, 10)}</span>
          </span>
        ) : (
          <span className="text-xs text-muted-foreground" data-testid="reference-run-info">Aucun run disponible (comparaison structurelle seule)</span>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <Input value={runInput} onChange={(e) => setRunInput(e.target.value)} placeholder="run_id explicite…"
            className="rounded-sm h-8 w-56 font-mono text-xs" data-testid="run-id-input" />
          <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={applyRun} data-testid="apply-run-btn">Appliquer</Button>
          {runId && <Button size="sm" variant="ghost" className="rounded-sm h-8" onClick={() => { setRunId(""); setRunInput(""); }} data-testid="reset-run-btn">Dernier run</Button>}
        </div>
      </div>

      {/* Toggle divergences */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-muted-foreground font-mono" data-testid="twin-summary">
          {comp?.summary ? `${comp.summary.total} nœud(s) · ${comp.summary.divergent} divergent(s) · ${comp.summary.unknown} non observé(s) · ${comp.summary.stale} périmé(s)` : ""}
        </div>
        <Button size="sm" variant={divOnly ? "default" : "outline"} className="rounded-sm gap-2"
          onClick={() => setDivOnly((v) => !v)} data-testid="divergences-only-toggle">
          <Filter className="h-3.5 w-3.5" /> {divOnly ? "Divergences seulement" : "Tous les nœuds"}
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground font-mono">Chargement…</p>
      ) : rows.length === 0 ? (
        <EmptyState icon={Radar} title="Aucune donnée" hint="Ajoutez des observations ou ajustez le filtre." />
      ) : (
        <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid="twin-table">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead>Nœud</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Structurel</TableHead>
                <TableHead>Observé</TableHead>
                <TableHead>Fraîcheur</TableHead>
                <TableHead>Simulé</TableHead>
                <TableHead>Divergence</TableHead>
                <TableHead className="text-right">Pourquoi ?</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.node_id} className="border-border" data-testid={`twin-row-${r.node_id}`}>
                  <TableCell className="font-medium">{r.name || r.node_id}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{r.node_type === "asset" ? "Actif" : "Service"}</TableCell>
                  <TableCell><StateCell value={r.structural_state} /></TableCell>
                  <TableCell><StateCell value={r.observed_state} /></TableCell>
                  <TableCell><Badge variant="outline" className={`rounded-sm text-xs ${twinFreshnessCls(r.observed_freshness)}`}>{tFreshness(r.observed_freshness)}</Badge></TableCell>
                  <TableCell><StateCell value={r.simulated_state} /></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`rounded-sm text-xs ${twinSeverityCls(r.severity)}`}>{tTwinSeverity(r.severity)}</Badge>
                      <span className="text-xs text-muted-foreground">{tDivergence(r.divergence)}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="rounded-sm h-7 w-7 p-0" onClick={() => setTrace(r)} data-testid={`twin-why-${r.node_id}`}>
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Dialog saisie manuelle */}
      <Dialog open={obsOpen} onOpenChange={setObsOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="observation-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Nouvelle observation</DialogTitle>
            <DialogDescription>Observation QUALIFIÉE d'un nœud (état réel constaté). Vide = horodatée maintenant.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label className="text-xs">Nœud</Label>
              <Select value={form.node_id} onValueChange={(v) => setForm({ ...form, node_id: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="obs-node-select"><SelectValue placeholder="Choisir un nœud…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {nodes.map((n) => <SelectItem key={n.id} value={n.id}>{n.name} — {n.type === "asset" ? "Actif" : "Service"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">État observé</Label>
              <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="obs-state-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SIMULATED_STATE_KEYS.map((k) => <SelectItem key={k} value={k}>{SIMULATED_STATE[k]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Confiance</Label>
              <Select value={form.confidence} onValueChange={(v) => setForm({ ...form, confidence: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="obs-confidence-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TWIN_CONFIDENCE_KEYS.map((k) => <SelectItem key={k} value={k}>{TWIN_CONFIDENCE[k]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Constaté le (optionnel)</Label>
              <Input type="datetime-local" value={form.observed_at} onChange={(e) => setForm({ ...form, observed_at: e.target.value })}
                className="mt-1 rounded-sm" data-testid="obs-observed-at-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Note</Label>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1 rounded-sm" rows={2} data-testid="obs-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setObsOpen(false)}>Annuler</Button>
            <Button className="rounded-sm" onClick={() => submitObs.mutate()} disabled={!form.node_id || submitObs.isPending} data-testid="save-observation-btn">
              {submitObs.isPending ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog causal trace */}
      <Dialog open={!!trace} onOpenChange={(v) => !v && setTrace(null)}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="twin-trace-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Pourquoi ? — {trace?.name}</DialogTitle>
            <DialogDescription>Trace causale déterministe (moteur PUR core/twin).</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1 max-h-96 overflow-y-auto cros-scroll">
            {(trace?.trace || []).map((t, i) => (
              <div key={`${t.rule_id}-${t.dimension}-${t.to}`} className="border border-border rounded-sm p-3 text-sm" data-testid={`twin-trace-row-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="rounded-sm font-mono text-[11px]">{t.rule_id}</Badge>
                  <span className="text-[11px] text-muted-foreground">{t.dimension}</span>
                </div>
                <div className="text-xs text-muted-foreground">{t.from ?? "—"} → <span className="text-foreground">{t.to}</span></div>
                <div className="text-xs mt-1">{t.justification}</div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button className="rounded-sm" onClick={() => setTrace(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TwinImportDialog open={importOpen} onOpenChange={setImportOpen}
        onDone={() => qc.invalidateQueries({ queryKey: ["twin-comparison"] })} />
    </AppShell>
  );
}

function TwinImportDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [maxMb, setMaxMb] = useState(50);

  React.useEffect(() => {
    if (!open) return;
    api.get("/config/twin").then(({ data }) => data?.max_upload_mb && setMaxMb(data.max_upload_mb)).catch(() => {});
  }, [open]);

  const submit = async () => {
    if (!file) return;
    if (file.size > maxMb * 1024 * 1024) { toast.error(`Fichier trop volumineux. Maximum : ${maxMb} Mo.`); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/twin/observations/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.ingested} observation(s) enregistrée(s)`);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const reset = () => { setFile(null); setResult(null); setBusy(false); };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="twin-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Import d'observations — CSV / JSON</DialogTitle>
          <DialogDescription>
            Colonnes : <code className="font-mono text-[11px]">node_id, state, source_type, confidence, observed_at, note</code>.
            Max <span data-testid="twin-import-max">{maxMb}</span> Mo. L'état ∈ 7 états figés.
          </DialogDescription>
        </DialogHeader>
        {!result ? (
          <label className="flex flex-col items-center justify-center border border-dashed border-border rounded-sm py-10 cursor-pointer hover:bg-secondary/40 transition-colors">
            <UploadCloud className="h-8 w-8 text-muted-foreground mb-3" />
            <span className="text-sm">{file ? file.name : "Choisir un fichier .csv ou .json"}</span>
            <input type="file" accept=".csv,.json" className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="twin-file-input" />
          </label>
        ) : (
          <div className="space-y-2 py-2" data-testid="twin-import-result">
            <div className="text-green-400 font-medium">{result.ingested} observation(s) enregistrée(s), {result.reconciled} réconciliée(s)</div>
            {result.error_count > 0 && (
              <ul className="mt-1 space-y-1 max-h-32 overflow-y-auto cros-scroll text-orange-400">
                {result.errors.map((er) => <li key={`${er.node_id}-${er.error}`} className="font-mono text-xs">{er.node_id}: {er.error}</li>)}
              </ul>
            )}
          </div>
        )}
        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Annuler</Button>
              <Button className="rounded-sm gap-2" onClick={submit} disabled={!file || busy} data-testid="twin-import-submit">
                <FileText className="h-4 w-4" /> {busy ? "Import…" : "Importer"}
              </Button>
            </>
          ) : (
            <Button className="rounded-sm" onClick={() => onOpenChange(false)}>Fermer</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
